import {
  Component,
  inject,
  OnInit,
  signal,
  computed,
  effect,
} from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import {
  PageHeaderComponent,
  BreadcrumbItem,
} from 'src/app/shared/components/page-header/page-header.component';
import { ActivatedRoute, Router } from '@angular/router';
import { TripHeaderComponent } from './trip-header/trip-header.component';
import { PrestationSectionComponent } from './prestation-section/prestation-section.component';
import { TripService } from '../../services/trip.service';
import { MealSectionComponent } from './meal-section/meal-section.component';
import { WasteSectionComponent } from './waste-section/waste-section.component';
import { TripCreationModalComponent } from '../trip-creation-modal/trip-creation-modal/trip-creation-modal.component';
import { NotificationService } from 'src/app/core/services/notification.service';
import { PrestationModalService } from '../../services/prestation-modal.service';
import { ValidationTranslatorService } from '../../services/validation-translator.service';
import { SectionValidationService } from '../../services/section-validation.service';
import { TripValidationBadgeComponent } from '../trip-validation-badge/trip-validation-badge.component';
import { DialogModule } from 'primeng/dialog';
import { FactorService } from 'src/app/factors/services/factor.service';
import { ParametersService } from '../../services/parameters.service';

@Component({
  selector: 'app-trip-dashboard',
  imports: [
    TranslocoModule,
    ButtonModule,
    TooltipModule,
    PageHeaderComponent,
    TripHeaderComponent,
    PrestationSectionComponent,
    MealSectionComponent,
    WasteSectionComponent,
    TripCreationModalComponent,
    TripValidationBadgeComponent,
    DialogModule,
  ],
  templateUrl: './trip-dashboard.component.html',
})
export class TripDashboardComponent implements OnInit {
  activatedRoute = inject(ActivatedRoute);
  tripService = inject(TripService);
  router = inject(Router);
  notificationService = inject(NotificationService);
  prestationModalService = inject(PrestationModalService);
  validationTranslator = inject(ValidationTranslatorService);
  sectionValidationService = inject(SectionValidationService);
  factorService = inject(FactorService);
  parametersService = inject(ParametersService);

  isCalculating = signal(false);
  breadcrumbs: BreadcrumbItem[] = [];

  factorUpdatedAfterTrip = false;

  displayWarningDialog = false;

  constructor() {
    // Update breadcrumbs when trip changes
    effect(() => {
      this.tripService.trip(); // This triggers the effect when trip changes
      this.setupBreadcrumbs();
      const factorLastUpdate = this.parametersService.lastUpdatedAt();
      const tripLastUpdate = this.tripService.trip()?.updatedAt;

      if (!!tripLastUpdate && !!factorLastUpdate) {
        this.factorUpdatedAfterTrip =
          factorLastUpdate > new Date(tripLastUpdate);
        this.displayWarningDialog = this.factorUpdatedAfterTrip;
      }
    });
  }

  // Computed property for canFinalize logic
  canFinalize = computed(() => {
    const report = this.tripService.validationReport();
    if (!report || report.status === 'errors') return false;

    return true;
  });

  async ngOnInit() {
    this.factorService.loadIfNeeded();
    const id = this.activatedRoute.snapshot.paramMap.get('id');
    if (id) {
      // Only load trip - validation will be loaded automatically
      this.tripService.loadTrip(id);
      this.parametersService.LoadLastUpdated();
      // Breadcrumbs are now handled by the effect in constructor
    }
  }

  private setupBreadcrumbs() {
    const trip = this.tripService.trip();
    // Only update breadcrumbs if we have a trip loaded
    if (trip) {
      this.breadcrumbs = [
        {
          label: 'Home',
          route: '/home',
          translationKey: 'navigation.home',
        },
        {
          label: 'Trips',
          route: '/home/travel-board',
          translationKey: 'navigation.trips',
        },
        {
          label: trip.title || 'Trip',
        },
      ];
    }
  }

  hasAnyPrestations(): boolean {
    return this.tripService.hasAnyPrestations();
  }

  // Check if the trip can be calculated (has prestations and passes validation)
  canCalculate(): boolean {
    return this.hasAnyPrestations() && this.canFinalize();
  }

  /**
   * Scroll to and open the modal for the first invalid item
   * This is triggered when user clicks on the validation badge
   */
  scrollToFirstInvalidItem = (): void => {
    const report = this.tripService.validationReport();
    if (!report || report.issues.length === 0) return;

    // Find first error, or first warning if no errors
    const firstError = report.issues.find(
      (issue) => issue.severity === 'error',
    );
    const firstIssue = firstError || report.issues[0];

    // Navigate to the appropriate modal based on scope
    switch (firstIssue.scope) {
      case 'transport':
        if (firstIssue.index !== undefined) {
          this.prestationModalService.editTransport(firstIssue.index);
        } else {
          this.prestationModalService.addTransport();
        }
        break;
      case 'accommodation':
        if (firstIssue.index !== undefined) {
          this.prestationModalService.editAccommodation(firstIssue.index);
        } else {
          this.prestationModalService.addAccommodation();
        }
        break;
      case 'activity':
        if (firstIssue.index !== undefined) {
          this.prestationModalService.editActivity(firstIssue.index);
        } else {
          this.prestationModalService.addActivity();
        }
        break;
      case 'meal':
        if (firstIssue.index !== undefined) {
          this.prestationModalService.editMeal(firstIssue.index);
        } else {
          this.prestationModalService.addMeal();
        }
        break;
      case 'trip':
        // Open trip settings modal
        // Note: we would need to inject TripCreationModalService if needed
        break;
    }
  };

  calculateImpact() {
    const trip = this.tripService.trip();
    if (!trip) {
      this.notificationService.error('Aucun voyage trouvé');
      return;
    }
    this.isCalculating.set(true);
    this.tripService.updateTrip(trip).subscribe({
      next: (response) => {
        this.isCalculating.set(false);
        this.tripService.trip.set(response.trip);
        // Navigate to the new impact page
        this.router.navigate([`/home/trips/${trip.id}/impact`]);
      },
      error: () => {
        // console.error('updateTrip error:', error);
        this.isCalculating.set(false);
        // this.notificationService.error('Erreur lors du calcul de l\'impact');
      },
    });
  }
}
